import { getAccessToken } from '../authentication/supabaseService';

export interface Menu {
  id: string;
  restaurantId: string;
  name: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface MenuCreateRequest {
  restaurantId: string;
  name: string;
  isActive?: boolean;
}

export interface MenuUpdateRequest {
  id: string;
  name?: string;
  isActive?: boolean;
}

function mapMenu(data: any): Menu {
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function fetchMenusByRestaurant(restaurantId: string): Promise<Menu[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/menus/restaurant/${restaurantId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to fetch menus: ${res.status} ${res.statusText}`);
  const data = await res.json();
  return data.map(mapMenu);
}

export async function createMenu(request: MenuCreateRequest): Promise<Menu> {
  const token = await getAccessToken();
  const res = await fetch('/api/menus', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`Failed to create menu: ${res.status} ${res.statusText}`);
  return mapMenu(await res.json());
}

export async function updateMenu(request: MenuUpdateRequest): Promise<Menu> {
  const token = await getAccessToken();
  const res = await fetch(`/api/menus/${request.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`Failed to update menu: ${res.status} ${res.statusText}`);
  return mapMenu(await res.json());
}

export async function deleteMenu(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/menus/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to delete menu: ${res.status} ${res.statusText}`);
}

export async function addItemsToMenu(menuId: string, costPriceIds: string[]): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/menus/${menuId}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ costPriceIds }),
  });
  if (!res.ok) throw new Error(`Failed to add items to menu: ${res.status} ${res.statusText}`);
}

export async function removeItemsFromMenu(menuId: string, costPriceIds: string[]): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/menus/${menuId}/items`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ costPriceIds }),
  });
  if (!res.ok) throw new Error(`Failed to remove items from menu: ${res.status} ${res.statusText}`);
}

export async function fetchMenuItems(menuId: string): Promise<string[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/menus/${menuId}/items`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to fetch menu items: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function fetchMenuItemNames(menuId: string): Promise<string[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/menus/${menuId}/item-names`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to fetch menu item names: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function fetchMenusForCostPriceItem(costPriceId: string): Promise<string[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/menus/cost-price-item/${costPriceId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to fetch menus for cost price item: ${res.status} ${res.statusText}`);
  return res.json();
}
